import React, { Component } from 'react';
import axios from 'axios';
import queryString from 'query-string';
import { TextField, Button, CircularProgress } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Radio from '@material-ui/core/Radio';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';

import { endpoints } from '../../config';
import { ILanguageStrings } from '../../utils/languages/languages';
import { IEmailVerifyResponse } from './EmailCheck';
import { StepperConfig } from '../../types/StepperConfig.types';
import { UserStatus } from '../../types/UserInfo.types';
const stepperConfig = new StepperConfig();

interface EmailValidationCodeProps {
  onCancel: Function;
  nextStep: Function;
  handleChange: Function;
  email: string;
  companyKey: string;
  languageStrings: ILanguageStrings;
  language: string;
  userAccounts: IEmailVerifyResponse[];
  onStepRemove: Function;
  rkey: string;
  okey: string;
  portalId: string;
  contractId: string;
  stepList: string[];
}

export class EmailValidationCode extends Component<EmailValidationCodeProps, {}> {
  state = {
    validationCode: '',
    validationCodeError: '',
    termsOfUseError: '',
    submitting: false,
    alertOpen: false,
    openAccountsModal: false,
    event: 'Test event',
    isActiveAccountModalOpened: false,
    radioValue: '0',
    isUserNotInvited: false,
    supplierCompanyKey: 0,
    personKey: 0
  };

  /**
   * Active supplier users should see Products & Services, Diversity and Company Info steps if they are available.
   * If not, simply tag the user and send it to the login page.
   * @param selectedAccount The selected account from the list
   */
  handleTagActiveUser(selectedAccount: IEmailVerifyResponse) {
    const { stepList } = this.props;
    const checkerSteps = [stepperConfig.Products, stepperConfig.Diversity, stepperConfig.BusinessInfo];
    const shouldContinueRegistration = checkerSteps.some(step => stepList.includes(step));
    if (shouldContinueRegistration) {
      setTimeout(() => {
        this.props.onStepRemove([stepperConfig.UserInfo, stepperConfig.SelectCompany, stepperConfig.Login]);
        this.handlePersonKeyChange(selectedAccount.personKey);
        this.handlePersonStatusChange(selectedAccount.status);
        this.handleUserNameChange(selectedAccount.userName);
        this.props.nextStep();
      }, 100);
    } else {
      this.setState({ isActiveAccountModalOpened: true });
    }
  }

  nextStepSingleAccount = (e: { preventDefault: Function; stopPropagation: Function }) => {
    if (this.state.validationCode.length === 0) {
      this.setState({ validationCodeError: this.props.languageStrings.emailValidationCode.validationErrorMessage });
    } else if (this.state.validationCode.length > 0) {
      const params = queryString.parse(window.location.search);
      const postData = {
        email: this.props.email,
        companyKey: params.companyKey,
        code: this.state.validationCode
      };

      this.setState({ validationCodeError: '', submitting: true });
      axios
        .post(endpoints.VerifyEmail, postData)
        .then(res => {
          this.setState({ submitting: false });
          if (res.status === 200) {
            e.preventDefault();
            this.props.userAccounts && this.props.userAccounts.length > 1
              ? this.setState({ openAccountsModal: true })
              : this.props.nextStep();
          }
        })
        .catch(() => {
          const validationCodeError = this.props.languageStrings.emailValidationCode.incorrectValidationCode;
          this.setState({ validationCodeError, submitting: false });
        });
    }
  };

  nextStepSeveralAccounts = () => {
    const newEvent = {
      target: {
        value: this.props.userAccounts.map(account => account.supplierCompanyKey)
      }
    };

    this.props.handleChange('companySelection', 'selectedSupplierCompanyKey')(newEvent);
    const selectedAccount = this.props.userAccounts[Number(this.state.radioValue)];
    this.handleRegisterResponse(selectedAccount);

    if (selectedAccount.status === UserStatus.active) {
      this.props.okey
        ? setTimeout(() => {
            this.props.onStepRemove([
              stepperConfig.UserInfo,
              stepperConfig.SelectCompany,
              stepperConfig.BusinessInfo,
              stepperConfig.Diversity,
              stepperConfig.Products,
              stepperConfig.Login
            ]);
            this.props.nextStep();
          }, 100)
        : this.handleTagActiveUser(selectedAccount);
    } else {
      setTimeout(() => this.props.nextStep(), 100);
    }
  };

  handlePersonKeyChange = (personKey: string) => {
    // add the personKey to UserInfo step
    const newEvent = {
      target: {
        value: personKey
      }
    };
    // adding personkey to userInfo state
    this.props.handleChange('userInfo', 'personKey')(newEvent);
  };

  handlePersonStatusChange = (status: string) => {
    // add status to UserInfo step
    const newEvent = {
      target: {
        value: status
      }
    };
    // adding status to userInfo state
    this.props.handleChange('userInfo', 'status')(newEvent);
  };

  handleAddressKeyChange = (addresskey: number) => {
    // add addressKey to companyLocation step
    const newEvent = {
      target: {
        value: {
          addresskey
        }
      }
    };
    // adding status to userInfo state
    this.props.handleChange('companySelection', 'selectLocation')(newEvent);
  };

  handleUserNameChange = (username: string = '') => {
    const newEvent = {
      target: {
        value: username
      }
    };
    // adding status to userInfo state
    this.props.handleChange('confirmation', 'username')(newEvent);
  };

  handleRegisterResponse = (response: IEmailVerifyResponse) => {
    if (response.personKey !== null) {
      // personkey exists, user already registered. Removing UserInfo and Login steps
      this.handlePersonKeyChange(response.personKey);
      this.handlePersonStatusChange(response.status);
      this.handleAddressKeyChange(response.addressKey);
      if (response.supplierCompanyKey) {
        if (response.status === UserStatus.invited && this.props.okey) {
          this.props.onStepRemove(stepperConfig.RequestAccess);
        }
        if (response.status === UserStatus.active) {
          this.setState({ supplierCompanyKey: response.supplierCompanyKey, personKey: response.personKey });
        }
        const newEvent = {
          target: {
            value: {
              companyKey: response.supplierCompanyKey
            }
          }
        };
        this.props.handleChange('companySelection', 'company')(newEvent);
      }
    }
  };

  resendCode = (e: any) => {
    e.preventDefault();
    axios
      .post(endpoints.RegisterEmail, {
        email: this.props.email,
        companyKey: this.props.companyKey,
        lang: this.props.language
      })
      .then(res => {
        this.setState({ alertOpen: true });
      });
  };

  handleSubmit = (e: { preventDefault: Function; stopPropagation: Function }) => {
    e.preventDefault();
    e.stopPropagation();
    this.state.openAccountsModal ? this.nextStepSeveralAccounts() : this.nextStepSingleAccount(e);
  };

  renderTermsOfUse = () => {
    return (
      <div className="terms">
        <p>
          {this.props.languageStrings.formatString(this.props.languageStrings.emailValidationCode.aknowledge, {
            terms: (
              <a className="primary" href="https://www.woodmac.com/siteassets/wm_brand/policy-and-privacy-pdf/wm_sc_scip_tous_202367508.pdf" target="_blank" rel="noreferrer">
                {this.props.languageStrings.emailValidationCode.termsOfUse}
              </a>
            )
          })}
        </p>
      </div>
    );
  };

  checkTagCompany = async () => {
    await axios.post(`${endpoints.CompanyInfo}/tagSupplierCompany`, {
      supplierCompanyKey: this.state.supplierCompanyKey,
      personKey: this.state.personKey,
      companyKey: this.props.companyKey
    });
  };

  renderSeveralAccountsModal = () => {
    if (this.state.openAccountsModal) {
      return (
        <Container>
          <Dialog
            open={this.state.isActiveAccountModalOpened}
            onClose={() => this.setState({ isActiveAccountModalOpened: false })}
          >
            <DialogContent>
              <Typography align="center">
                {this.props.languageStrings.emailValidationCode.modalActiveAccount}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Grid container direction="row" justifyContent="space-evenly">
                <Button
                  onClick={() => {
                    this.checkTagCompany();
                    this.setState({ isActiveAccountModalOpened: false });
                    window.location.href = '/';
                  }}
                  variant="contained"
                  color="primary"
                >
                  {this.props.languageStrings.requestAccess.modalLoginButton}
                </Button>
              </Grid>
            </DialogActions>
          </Dialog>
          <br />
          <br />
          <br />
          <Typography gutterBottom>
            {this.props.languageStrings.formatString(
              this.props.languageStrings.emailValidationCode.modalEmailExistsText,
              {
                email: <b>{this.props.email}</b>
              }
            )}
          </Typography>
          <Typography gutterBottom align="center">
            {this.props.languageStrings.formatString(
              this.props.languageStrings.emailValidationCode.modalEmailExistsGuide
            )}
          </Typography>
          <br />
          <br />
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Person Name</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Work Phone</TableCell>
                  <TableCell>Company Address</TableCell>
                  <TableCell>Username</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.props.userAccounts.length > 0 &&
                  this.props.userAccounts
                    .filter(item => item.addressKey !== 0)
                    .map((account: IEmailVerifyResponse, key: number) => (
                      <TableRow key={key}>
                        <TableCell>
                          <Radio
                            value={key}
                            data-testid={`radio-${key}`}
                            checked={this.state.radioValue === String(key)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              this.setState({ radioValue: (event.target as HTMLInputElement).value })
                            }
                          />
                        </TableCell>
                        <TableCell>{account.status}</TableCell>
                        <TableCell>{account.personName}</TableCell>
                        <TableCell>{account.companyName}</TableCell>
                        <TableCell>{account.workPhone}</TableCell>
                        <TableCell>{account.status === UserStatus.invited ? '' : account.companyAddress}</TableCell>
                        <TableCell>{account.status === UserStatus.invited ? '' : account.userName}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <br />
          <br />
          <Grid container justifyContent="center">
            <Typography
              align="center"
              color="secondary"
              onClick={() => this.props.nextStep()}
              style={{ cursor: 'pointer' }}
            >
              {this.props.languageStrings.emailValidationCode.newUserAccountLabel}
            </Typography>
          </Grid>
        </Container>
      );
    }
  };

  /**
   * User not invited text array has a placeholder {role}. This function replaces that placeholder dynamically.
   * @param paragraph The paragraph to format
   */
  getUserNotInvitedRoleText(paragraph: string) {
    const { okey, portalId, contractId } = this.props;
    let role = '';

    if (typeof okey === 'string' && okey.length) {
      role = this.props.languageStrings.requestAccess.eventCoordinator;
    }

    if (typeof portalId === 'string' && portalId.length) {
      role = this.props.languageStrings.requestAccess.portalOwner;
    }

    if (typeof contractId === 'string' && contractId.length) {
      role = this.props.languageStrings.requestAccess.contractOwner;
    }

    return this.props.languageStrings.formatString(paragraph, { role });
  }

  renderRequestAccessModals = () => {
    const modalNotInvitedTextLength = this.props.languageStrings.requestAccess.modalEmailNotInvitedText.length;
    return (
      <>
        <Dialog open={this.state.isUserNotInvited} onClose={() => this.setState({ isUserNotInvited: false })}>
          <DialogContent>
            {this.props.languageStrings.requestAccess.modalEmailNotInvitedText.map(
              (paragraph: string, index: number) => {
                if (index === modalNotInvitedTextLength - 1) {
                  // last paragraph has the {role} placeholder
                  return (
                    <React.Fragment key={`par-${index}`}>
                      <Typography align="center">{this.getUserNotInvitedRoleText(paragraph)}</Typography>
                      <br />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={`par-${index}`}>
                    <Typography align="center">{paragraph}</Typography>
                    <br />
                  </React.Fragment>
                );
              }
            )}
          </DialogContent>
          <DialogActions>
            <Grid container direction="row" justifyContent="space-evenly">
              <Button
                onClick={() => {
                  this.setState({ isUserNotInvited: false, submitted: false });
                }}
                variant="outlined"
                color="primary"
              >
                {this.props.languageStrings.requestAccess.modalCancelButton}
              </Button>
              <Button
                onClick={() => {
                  this.setState({ isUserNotInvited: false });
                }}
                variant="contained"
                color="primary"
              >
                {this.props.languageStrings.requestAccess.modalOkButton}
              </Button>
            </Grid>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  render() {
    return (
      <div className="EmailValidationCode">
        <div className="Wrapper">
          {this.renderSeveralAccountsModal()}
          {this.renderRequestAccessModals()}
          <form style={{ width: '100%' }} onSubmit={this.handleSubmit}>
            {this.state.openAccountsModal || (
              <>
                {this.state.alertOpen && (
                  <MuiAlert onClose={() => this.setState({ alertOpen: false })} severity="info">
                    {this.props.languageStrings.emailValidationCode.reSentLabel}
                  </MuiAlert>
                )}
                <p className="instructions">
                  {this.props.languageStrings.formatString(
                    this.props.languageStrings.emailValidationCode.emailValidationTitle,
                    {
                      email: <strong>{this.props.email}</strong>
                    }
                  )}
                </p>
                <TextField
                  size="medium"
                  label={this.props.languageStrings.emailValidationCode.validationCodeLabel}
                  onChange={event => this.setState({ validationCode: event.target.value })}
                  error={this.state.validationCodeError.length > 0}
                  helperText={this.state.validationCodeError}
                  variant="outlined"
                  inputProps={{ 'data-testid': 'validationCode' }}
                />

                <div className="resend">
                  <p>
                    <a className="primary" href="/" onClick={e => this.resendCode(e)}>
                      {this.props.languageStrings.emailValidationCode.resendCodeLabel}
                    </a>
                  </p>
                </div>

                {this.renderTermsOfUse()}
              </>
            )}
            <div className="Form-actions multiple">
              <Button variant="outlined" color="primary" onClick={e => this.props.onCancel(e)}>
                {this.props.languageStrings.backButtonLabel}
              </Button>

              <Button
                disabled={this.state.validationCode.length === 0 || this.state.submitting}
                type="submit"
                variant="contained"
                color="primary"
                data-testid="continue"
              >
                {this.state.submitting ? (
                  <CircularProgress style={{ width: '24px', height: '24px' }} />
                ) : (
                  <span>{this.props.languageStrings.continueButtonLabel}</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default EmailValidationCode;
